"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { UserCheck, Sparkles, Share2, ShieldCheck, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/cosmic/Reveal";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import styles from "../YourInfiniteSelves.module.css";
const STEPS = [{
  step: "01",
  title: "Share a nickname & optional answers",
  description: "Enter an optional nickname and tap a few interests, strengths, ambitions, or life paths. Leave anything blank — seeded picks will fill the void with plausible choices.",
  icon: UserCheck
}, {
  step: "02",
  title: "Explore your free preview",
  description: "Four fictional lives (Divergent, Ascendant, Strange, Unexpected) take shape in your browser with traits, origins, cosmic laws, and life at a glance right away.",
  icon: Sparkles
}, {
  step: "03",
  title: "Read the full Multiverse Life Dossier",
  description: "All complete life chapters—biography, work, standing, home, society, technology, Earth comparison, day in the life, futures, timeline—are free to explore, and you can download a full PDF.",
  icon: Share2
}];
export default function HowItWorks() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="how-it-works" data-nav="dark" className="relative w-full max-w-full overflow-hidden bg-transparent text-foreground py-14 sm:py-16 md:py-24 border-b border-border">
      <div className="relative mx-auto max-w-[76rem] px-4 sm:px-6 lg:px-8 w-full">
        <Reveal>
          <div className="flex flex-col items-center text-center mb-16">
            <SectionHeading eyebrow="PROCESS & ARCHITECTURE" title="How Your Selves Take Shape" description="Each run generates four complete, fictional alternate lives in your browser with no AI service. 'Explore Another Possibility' draws a fresh coherent set, while your settings code and variation ID always reconstruct the identical four lives." align="center" as="h2" />
          </div>
        </Reveal>

        {/* Stepper Grid with glowing connectors */}
        <div className="relative mb-12">
          {/* Desktop horizontal connecting line behind step nodes */}
          <div aria-hidden="true" className={`hidden md:block absolute top-12 left-[16%] right-[16%] h-[2px] ${styles.stepperLine} z-0 opacity-70`} />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 relative z-10">
            {STEPS.map((item, index) => {
            const Icon = item.icon;
            return <motion.div key={item.step} initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.12
            }} className="ql-card p-6 sm:p-7 flex flex-col justify-between group">
                  <div>
                    <div className="flex items-center justify-between mb-4 sm:mb-5">
                      <div className="size-10 sm:size-11 rounded-full bg-primary/10 border border-primary/40 flex items-center justify-center text-primary shadow-[0_0_12px_var(--glow-primary)] group-hover:scale-105 transition-transform duration-200 shrink-0">
                        <span className="font-mono text-xs sm:text-sm font-bold tracking-wider">
                          {item.step}
                        </span>
                      </div>
                      <div className="size-9 sm:size-10 rounded-full bg-muted/60 border border-border flex items-center justify-center text-primary group-hover:text-foreground transition-colors shrink-0">
                        <Icon className="size-4 sm:size-5" aria-hidden="true" />
                      </div>
                    </div>

                    <h3 className="font-serif text-base sm:text-xl font-bold text-card-foreground mb-2 sm:mb-3 leading-snug break-words">
                      {item.title}
                    </h3>

                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed break-words">
                      {item.description}
                    </p>
                  </div>

                  <div className="pt-4 mt-6 border-t border-border/40 flex items-center justify-between text-xs font-mono text-primary/80">
                    <span>STAGE {item.step}</span>
                    <ArrowRight className="size-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Browser-only assurance banner */}
        <Reveal delay={200}>
          <div className="ql-glass p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-center gap-3 text-center sm:text-left border-border">
            <ShieldCheck className="size-5 text-primary shrink-0" aria-hidden="true" />
            <p className="text-sm text-foreground/90 font-medium">
              The free generator runs client-side from deterministic seeded narrative models, with the full dossier and PDF export free, nothing sent to a server.
            </p>
          </div>
        </Reveal>
      </div>
    </section>;
}